import React from 'react';
import { Link } from 'react-router-dom';
import Glp1BridgeResourceNav from './Glp1BridgeResourceNav';
import { captureResourceOpen } from '../utils/analytics';

const resourceCards = [
  {
    label: 'Provider Tool',
    title: 'GLP-1 Bridge screening tool and AI assistant',
    description: 'Use the provider-gated screening tool and assistant for de-identified case support and CMS-grounded workflow questions.',
    action: 'Open AI assistant',
    to: '/resources/glp1-bridge/assistant',
    analyticsSlug: 'glp1-bridge-assistant',
    resourceType: 'assistant',
  },
  {
    label: 'Recording + Slides',
    title: 'Bridge webinar recording',
    description: 'Watch the GLP-1 Bridge walkthrough and review the 28-slide webinar deck.',
    action: 'Watch recording',
    to: '/resources/glp1-bridge/recording',
    analyticsSlug: 'glp1-bridge-recording',
    resourceType: 'recording',
  },
  {
    label: 'Clinical Guide',
    title: 'Medicare GLP-1 Bridge guide',
    description: 'Eligibility, covered products, pharmacy billing, PA criteria, and CMS source links.',
    action: 'Open clinical guide',
    to: '/resources/glp1-bridge/guide',
    analyticsSlug: 'glp1-bridge-guide',
    resourceType: 'clinical-guide',
  },
];

const quickFacts = [
  { label: 'Program dates', value: 'Effective July 1, 2026 - Dec. 31, 2027' },
  { label: 'Products', value: 'Foundayo, Wegovy, Zepbound KwikPen' },
  { label: 'Patient copay', value: '$50/month' },
];

export default function Glp1BridgeHubPage() {
  return (
    <div className="min-h-screen bg-white">
      <section className="border-b border-[#e5e5ea] bg-[#f5f5f7] px-6 py-12 md:px-16">
        <div className="mx-auto max-w-[1180px]">
          <nav className="mb-7 flex items-center gap-2 text-sm text-[#86868b]">
            <Link to="/resources" className="hover:text-[#1d1d1f] transition-colors">Resources</Link>
            <span>/</span>
            <span className="text-[#1d1d1f]">Medicare GLP-1 Bridge</span>
          </nav>
          <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_240px] xl:items-start">
            <div className="min-w-0">
              <div className="max-w-none">
                <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                  Resource center
                </p>
                <h1 className="text-4xl font-semibold leading-tight tracking-normal text-[#1d1d1f] md:text-[46px] lg:whitespace-nowrap lg:text-[52px]">
                  Medicare GLP-1 Bridge resources
                </h1>
                <p className="mt-5 max-w-none text-[17px] leading-relaxed text-[#53535a] lg:whitespace-nowrap">
                  Choose the fastest path to learn the CMS Bridge rules, watch the walkthrough, or use the provider assistant.
                </p>
              </div>

              <dl className="mt-8 grid gap-px overflow-hidden rounded-lg border border-[#e5e5ea] bg-[#e5e5ea] md:grid-cols-3">
                {quickFacts.map((fact) => (
                  <div key={fact.label} className="bg-white px-4 py-4">
                    <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#86868b]">{fact.label}</dt>
                    <dd className="mt-1 text-sm font-semibold leading-snug text-[#1d1d1f]">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <Glp1BridgeResourceNav
              current="hub"
              helperText="Jump directly to a resource."
              layout="side"
              className="xl:sticky xl:top-24"
            />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[980px] px-6 py-12 md:px-16">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold tracking-normal text-[#1d1d1f]">What do you need today?</h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-[#6e6e73]">
            Each resource is focused on a different workflow: reference, training, or case support.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3" data-testid="glp1-bridge-resource-cards">
          {resourceCards.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              onClick={() =>
                captureResourceOpen(card.analyticsSlug, {
                  resource_title: card.title,
                  resource_type: card.resourceType,
                  source: 'glp1-bridge-hub',
                })
              }
              className="group flex min-h-[260px] flex-col rounded-lg border border-[#e5e5ea] bg-white p-6 transition hover:border-[#1d1d1f] hover:shadow-[0_18px_45px_rgba(15,23,42,0.08)]"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.08em] text-emerald-700">
                {card.label}
              </span>
              <h3 className="mt-4 text-[22px] font-semibold leading-tight text-[#1d1d1f]">
                {card.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[#6e6e73]">
                {card.description}
              </p>
              <span className="mt-auto inline-flex items-center gap-1 pt-8 text-sm font-semibold text-[#06c] transition group-hover:text-[#004aad]">
                {card.action}
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </span>
            </Link>
          ))}
        </div>

        <section className="mt-12 border-t border-[#e5e5ea] pt-8">
          <h2 className="text-xl font-semibold text-[#1d1d1f]">Source boundary</h2>
          <p className="mt-3 max-w-3xl text-[14px] leading-relaxed text-[#6e6e73]">
            Guide and assistant content is based on CMS Medicare GLP-1 Bridge guidance. Verify current CMS operational details before final PA submission or pharmacy routing.
          </p>
        </section>
      </main>
    </div>
  );
}
